import { useQuery, useQueryClient, useMutation } from 'react-query';
import { useActiveStore } from '../viewModel/stores/storeInformationStore';
import { useState } from 'react';

import { 
  fetchInventoryData,
  getOpportunitiesByStatus,
  updateOpportunityStatus,
  getOpportunitiesCount,
  fetchSupplyAdjustments,
  fetchOtbPerformanceByLevel,
  fetchConsolidatedOtb,
  fetchOtbsCycles,
  fetchInventoryDiagnosisData,
  fetchOtbSalesPlan,
  fetchOtbSalesPlanDetailed,
  fetchOtbDetailByLevel,
  saveOtbVersion,
  fetchOpportunitiesTrends,
  fetchOpportunitiesTrendsDetails,
  fetchOpportunitiesDetails,
  fetchMerchandiseAnalytics
} from "./services/OtbService";

import { OPPORTUNITY_STATUS } from '../components/components.constants';
import { CACHE_TTL_MS } from './ttl.cache';

const deduplicateObjects = (opportunities, key) => {
  const seenIds = new Set();
  return (opportunities ?? []).filter(opp => {
    const id = opp[key];
    if (id && !seenIds.has(id)) {
      seenIds.add(id);
      return true;
    }
    return false;
  });
};

const deduplicatePrimitives = (arr) => Array.from(new Set(arr ?? []));

const applyCounterDelta = (data, opp, delta) => {
  const base = Array.isArray(data) ? { count: 0, totalValue: 0, counter: [] } : data;
  const value = parseFloat(opp.value || 0);
  const hasType = base.counter.some(item => item.type === opp.type1);

  const counter = (hasType ? base.counter : [...base.counter, { type: opp.type1, count: 0, totalValue: 0 }])
    .map(item => item.type === opp.type1
      ? { ...item, count: item.count + delta, totalValue: item.totalValue + value * delta }
      : item)
    .filter(item => item.count > 0);

  return {
    ...base,
    count: base.count + delta,
    totalValue: base.totalValue + value * delta,
    counter,
  };
};

export const useOtb = (options = {}) => {

  const queryClient = useQueryClient();

  const { 
    shouldFetchActiveOpp = '',
    shouldFetchCheckoutOpp = '',
    shouldFetchRemovedOpp = '',
    shouldFetchInventory = false,
    shouldFecthOppCounter = false,
    shouldFetchSupplyAdjustments = false,
    shouldFetchOtbConsolidated = false,
    shouldFetchOtbDetail = false,
    shouldFetchOtbCycles = false,
    shouldFetchOtbPerformanceByLevel = false,
    shouldFetchInventoryDiagnosis = false,
    shouldFetchSalesPlan = false,
    productHierarchyNodeId = null,
    parentLevelId = null,
    performanceCycleId = null,
    cycleId = null,
    category = null,
    shouldFetchOpportunitiesDetails = false,
    shouldFetchMerchandiseAnalytics = false,
    page = 1,
    pageSize = 30
  } = options;

  const [updateStatusError, setUpdateStatusError] = useState(null);
  const [isUpdateStatusSuccess, setIsUpdateStatusSuccess] = useState(false);

  // open to buy
  const openToBuyConsolidated = useQuery({
    queryKey: ["open-to-buy-consolidated", cycleId],
    queryFn: async () => {
      const data = await fetchConsolidatedOtb(cycleId);
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_24_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    refetchOnMount: false,
    enabled: shouldFetchOtbConsolidated,
    ...options,
  });

  const firstCategoryCache = openToBuyConsolidated.data?.firstCategory;
  const isFirstCategorySelected = !!firstCategoryCache?.category && firstCategoryCache.category === category;

  const otbDetailByLevel = useQuery({
    queryKey: ["otb-detail-by-level", cycleId, category],
    queryFn: async () => {
      const data = await fetchOtbDetailByLevel(cycleId, category);
      return data;
    },
    initialData: () => (isFirstCategorySelected ? firstCategoryCache.detail : undefined),
    staleTime: CACHE_TTL_MS.TTL_24_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    refetchOnMount: false,
    enabled: shouldFetchOtbDetail && !!category && !isFirstCategorySelected
  });

  const otbCycles = useQuery({
    queryKey: ["otb-cycles"],
    queryFn: async () => {
      const data = await fetchOtbsCycles();
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_24_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    refetchOnMount: false,
    enabled: shouldFetchOtbCycles
  });

  const otbPerformanceByLevel = useQuery({
    queryKey: ["otb-performance-by-level", performanceCycleId],
    queryFn: async () => {
      const data = await fetchOtbPerformanceByLevel(performanceCycleId);
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_24_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    refetchOnMount: false,
    enabled: shouldFetchOtbPerformanceByLevel
  });

  const otbSalesPlan = useQuery({
    queryKey: ["otb-sales-plan"],
    queryFn: async () => {
      const data = await fetchOtbSalesPlan();
      return data;
    },
    staleTime: CACHE_TTL_MS.NO_CACHE,
    cacheTime: CACHE_TTL_MS.NO_CACHE,
    refetchOnMount: false,
    enabled: shouldFetchSalesPlan
  });

  const otbSalesPlanDetailed = useQuery({
    queryKey: ["otb-sales-plan-detailed", productHierarchyNodeId, parentLevelId],
    queryFn: async () => {
      const data = await fetchOtbSalesPlanDetailed(productHierarchyNodeId, parentLevelId);
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_24_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    refetchOnMount: false,
    enabled: !!productHierarchyNodeId && !!parentLevelId
  });

  const saveOtbVersionMutation = useMutation({
    mutationFn: async ({ cycleId, versionName, edits }) => {
      return await saveOtbVersion(cycleId, { versionName, edits });
    },

    onSuccess: (_data, { cycleId }) => {
      queryClient.invalidateQueries(['otb-detail-by-level', cycleId]);
      queryClient.refetchQueries(["otb-cycles"])
    },

    onError: (error) => {
      console.error('Erro ao salvar versão do OTB:', error);
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },
  });

  // estoques
  const inventoryQuery = useQuery({
    queryKey: ["inventory"],
    queryFn: async () => {
      const data = await fetchInventoryData();
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_3_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_1_HOUR,
    refetchOnMount: false,
    enabled: shouldFetchInventory,
    ...options,
  });

  const inventoryDiagnosisQuery = useQuery({
    queryKey: ["inventory_diagnosis"],
    queryFn: async () => {
      const data = await fetchInventoryDiagnosisData();
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_24_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    refetchOnMount: false,
    enabled: shouldFetchInventoryDiagnosis,
  });

  // oportunidades
  const removedOpportunities = useQuery({
    queryKey: ['removedOpportunities', shouldFetchRemovedOpp],
    queryFn: async () => {
      const response = await getOpportunitiesByStatus(shouldFetchRemovedOpp);
      return {
        removedOpportunities: deduplicateObjects(response.removedOpportunities, 'opportunityId'),
        removedProductIds: deduplicatePrimitives(response.removedProductIds),
        opportunityTypes: response.opportunityTypes ?? {},
        types: Object.keys(response.opportunityTypes ?? {}),
      };
    },
    enabled: !!shouldFetchRemovedOpp,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: CACHE_TTL_MS.TTL_1_HOUR,
    cacheTime: CACHE_TTL_MS.TTL_1_HOUR,
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    onError: (error) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },
  });

  const checkoutOpportunities = useQuery({
    queryKey: ['checkoutOpportunities', shouldFetchCheckoutOpp],
    queryFn: async () => {
      const response = await getOpportunitiesByStatus(shouldFetchCheckoutOpp);
      return {
        checkoutOpportunities: deduplicateObjects(response.checkoutOpportunities, 'opportunityId'),
        checkoutProductIds: deduplicatePrimitives(response.checkoutProductIds),
        opportunityTypes: response.opportunityTypes ?? {},
        types: Object.keys(response.opportunityTypes ?? {}),
      };
    },
    enabled: !!shouldFetchCheckoutOpp,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    staleTime: CACHE_TTL_MS.TTL_1_HOUR,
    cacheTime: CACHE_TTL_MS.TTL_1_HOUR,
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    onError: (error) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },
  });

  const activeOpportunities = useQuery({
    queryKey: ['activeOpportunities', shouldFetchActiveOpp],
    queryFn: async () => {
      const response = await getOpportunitiesByStatus(shouldFetchActiveOpp);
      return {
        activeOpportunities: deduplicateObjects(response.activeOpportunities, 'opportunityId'),
        activeProductIds: deduplicatePrimitives(response.activeProductIds),
        opportunityTypes: response.opportunityTypes ?? {},
        types: Object.keys(response.opportunityTypes ?? {}),
      };
    },
    enabled: !!shouldFetchActiveOpp,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: CACHE_TTL_MS.TTL_1_HOUR,
    cacheTime: CACHE_TTL_MS.TTL_1_HOUR,
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    onError: (error) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },
  });

  const updateOpportunityStatusMutation = useMutation({
    mutationFn: async ({ opportunityId, status, removalReason = '' }) => {
      return await updateOpportunityStatus({ opportunityId, status, removalReason });
    },

    onMutate: async ({ opportunityId, status }) => {
      const lists = [
        { status: OPPORTUNITY_STATUS.ACTIVE, prefix: 'activeOpportunities', field: 'activeOpportunities' },
        { status: OPPORTUNITY_STATUS.CHECKOUT, prefix: 'checkoutOpportunities', field: 'checkoutOpportunities' },
        { status: OPPORTUNITY_STATUS.REMOVED, prefix: 'removedOpportunities', field: 'removedOpportunities' },
      ];

      await Promise.all(lists.map(({ prefix }) => queryClient.cancelQueries([prefix])));

      const queryCache = queryClient.getQueryCache();

      const entries = lists.flatMap(list =>
        queryCache.findAll([list.prefix]).map(query => ({
          ...list,
          queryKey: query.queryKey,
          data: query.state.data,
        }))
      );

      const source = entries.find(({ data, field }) =>
        data?.[field]?.some(opp => opp.opportunityId === opportunityId)
      );
      const movedOpp = source?.data[source.field].find(opp => opp.opportunityId === opportunityId);
      const sourceStatus = source?.status;

      entries.forEach(({ status: listStatus, queryKey, field }) => {
        queryClient.setQueryData(queryKey, (old) => {
          if (!old) return old;
          const filtered = (old[field] ?? []).filter(opp => opp.opportunityId !== opportunityId);
          return {
            ...old,
            [field]: listStatus === status && movedOpp ? [...filtered, movedOpp] : filtered,
          };
        });
      });

      const previousCount = queryClient.getQueryData(['opportunities_count']);

      const leavesActive = sourceStatus === OPPORTUNITY_STATUS.ACTIVE && status !== OPPORTUNITY_STATUS.ACTIVE;
      const entersActive = sourceStatus !== OPPORTUNITY_STATUS.ACTIVE && status === OPPORTUNITY_STATUS.ACTIVE;
      const delta = leavesActive ? -1 : entersActive ? 1 : 0;

      if (previousCount !== undefined && movedOpp && delta !== 0) {
        queryClient.setQueryData(['opportunities_count'], applyCounterDelta(previousCount, movedOpp, delta));
      }

      return {
        previousEntries: entries.map(({ queryKey, data }) => ({ queryKey, data })),
        previousCount,
      };
    },

    onError: (error, _variables, context) => {
      console.error('Erro ao atualizar status da oportunidade:', error);

      context?.previousEntries?.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });

      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(['opportunities_count'], context.previousCount);
      }

      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },
  });

  const resetUpdateStatus = () => {
    setUpdateStatusError(null);
    setIsUpdateStatusSuccess(false);
  };

  const toggleTypeFilter = (type) => {
    queryClient.setQueryData(['checkoutOpportunities', shouldFetchCheckoutOpp], (oldData) => {
      if (!oldData) return oldData;
      const currentTypes = oldData.types || [];
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter((t) => t !== type)
        : [...currentTypes, type];
      return { ...oldData, types: newTypes };
    });

    queryClient.setQueryData(['activeOpportunities', shouldFetchActiveOpp], (oldData) => {
      if (!oldData) return oldData;
      const currentTypes = oldData.types || [];
      const newTypes = currentTypes.includes(type)
        ? currentTypes.filter((t) => t !== type)
        : [...currentTypes, type];
      return { ...oldData, types: newTypes };
    });
  };

  const fetchOpportunitiesCount = useQuery({
    queryKey: ["opportunities_count"],
    queryFn: async () => {
      const data = await getOpportunitiesCount();
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_30_MINUTES,
    cacheTime: CACHE_TTL_MS.TTL_15_MINUTES,
    enabled: shouldFecthOppCounter,
    refetchOnMount: false,
    ...options,
  });

  const fetchOpportunitiesTrendsData = useQuery({
    queryKey: ["opportunities_trends"],
    queryFn: async () => {
      const data = await fetchOpportunitiesTrends();
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_24_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    enabled: !!shouldFetchOpportunitiesDetails,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    onError: (error) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },
  });

  const fetchOpportunitiesTrendsDetailsData = useQuery({
    queryKey: ["opportunities_trends_details"],
    queryFn: async () => {
      const data = await fetchOpportunitiesTrendsDetails();
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_24_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    enabled: !!shouldFetchOpportunitiesDetails,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    onError: (error) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },
  });

  const fetchOpportunitiesDetailsData = useQuery({
    queryKey: ["opportunities_details"],
    queryFn: async () => {
      const data = await fetchOpportunitiesDetails();
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_24_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_24_HOURS,
    enabled: !!shouldFetchOpportunitiesDetails,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    onError: (error) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },
  });    

  // abastecimento
  const storeId = useActiveStore((state) => state.storeId);

  const fetchSupplyAdjustmentsData = useQuery({
    queryKey: ["supply_adjustments", storeId, page, pageSize],
    queryFn: async () => {
      const data = await fetchSupplyAdjustments(storeId, page, pageSize);
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_3_HOURS,
    cacheTime: CACHE_TTL_MS.TTL_1_HOUR,
    enabled: shouldFetchSupplyAdjustments && storeId != null && !!page && !!pageSize,
    refetchOnMount: false,
    ...options,
  });

  // analytics
  const merchandiseAnalyticsQuery = useQuery({
    queryKey: ["merchandise-analytics"],
    queryFn: async () => {
      const data = await fetchMerchandiseAnalytics();
      return data;
    },
    staleTime: CACHE_TTL_MS.TTL_15_MINUTES,
    cacheTime: CACHE_TTL_MS.TTL_1_HOUR,
    enabled: !!shouldFetchMerchandiseAnalytics,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
    onError: (error) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("exp");
        localStorage.removeItem("store-information");
        localStorage.removeItem("active-store");
        window.location.href = '/';
      }
    },
  });

  return {
    // estoques
    inventory: inventoryQuery,

    inventoryDiagnosis: inventoryDiagnosisQuery.data?.inventoryDiagnosis,
    purchaseOperation: inventoryDiagnosisQuery.data?.purchaseOperation,
    isLoadingInventoryDiagnosis: inventoryDiagnosisQuery.isLoading,

    // oportunidades
    activeOpportunities: activeOpportunities.data,
    isLoadingActiveOpportunities: activeOpportunities.isLoading,
    activeOpportunitiesError: activeOpportunities.error,

    removedOpportunities: removedOpportunities.data,
    isLoadingRemovedOpportunities: removedOpportunities.isLoading,

    checkoutOpportunities: checkoutOpportunities.data,
    isLoadingCheckoutOpportunities: checkoutOpportunities.isLoading,
    checkoutOpportunitiesError: checkoutOpportunities.error,

    updateOpportunityStatusAsync: updateOpportunityStatusMutation.mutateAsync,
    updateOpportunityStatusError: updateOpportunityStatusMutation.error,
    isUpdatingOpportunityStatus: updateOpportunityStatusMutation.isLoading,
    isUpdateOpportunityStatusSuccess: updateOpportunityStatusMutation.isSuccess,

    updateStatusError,
    isUpdateStatusSuccess,
    resetUpdateStatus,

    toggleTypeFilter,

    opportunitiesCount: fetchOpportunitiesCount.data,

    opportunitiesTrends: fetchOpportunitiesTrendsData.data,
    isLoadingOpportunitiesTrends: fetchOpportunitiesTrendsData.isLoading,

    opportunitiesTrendsDetails: fetchOpportunitiesTrendsDetailsData.data,
    isLoadingOpportunitiesTrendsDetails: fetchOpportunitiesTrendsDetailsData.isLoading,

    opportunitiesDetails: fetchOpportunitiesDetailsData.data,
    isLoadingOpportunitiesDetails: fetchOpportunitiesDetailsData.isLoading,

    merchandiseAnalytics: merchandiseAnalyticsQuery.data,
    isLoadingMerchandiseAnalytics: merchandiseAnalyticsQuery.isLoading,
    merchandiseAnalyticsError: merchandiseAnalyticsQuery.error,

    // abastecimento
    fetchSupplyAdjustmentsData: fetchSupplyAdjustmentsData.data,
    storesBreakdown: fetchSupplyAdjustmentsData.data?.storesBreakdown,

    isLoadingSupplyAdjustments: fetchSupplyAdjustmentsData.isLoading,
    isLoadingOppCount: fetchOpportunitiesCount.isLoading,

    // open to buy
    openToBuyDataConsolidated: openToBuyConsolidated.data,
    isLoadingOtbDataConsolidated: openToBuyConsolidated.isLoading,

    otbDetailByLevel: otbDetailByLevel.data,
    isLoadingOtbDetail: otbDetailByLevel.isLoading,

    otbCycles: otbCycles.data,
    isLoadingOtbCycles: otbCycles.isLoading,

    otbPerformanceByLevel: otbPerformanceByLevel.data,
    isLoadingOtbPerformanceByLevel: otbPerformanceByLevel.isLoading,

    otbSalesPlan: otbSalesPlan.data,
    isLoadingOtbSalesPlan: otbSalesPlan.isLoading,

    otbSalesPlanDetailed: otbSalesPlanDetailed.data,
    isLoadingOtbSalesPlanDetailed: otbSalesPlanDetailed.isLoading,

    saveOtbVersionAsync: saveOtbVersionMutation.mutateAsync,
    isSavingOtbVersion: saveOtbVersionMutation.isLoading,
    saveOtbVersionMutationError: saveOtbVersionMutation.error,
  };
};